import type { Resolvable } from '@/services/resolvers/types'
import TmLogicError from '@/core/error/tmLogicError'
import type { Route } from '@/services/route/types'

export function withUniqueKeys(description?: string) {
  return function <T extends { new (...args: any[]): Resolvable<any> }>(constructor: T) {
    // create WeakMap to store resolverUid for each instance
    const resolverUidMap = new WeakMap<any, symbol>()

    // save original getResolverKeys method
    const originalGetResolverKeys = constructor.prototype.getResolverKeys

    // override getResolverKeys method in prototype
    constructor.prototype.getResolverKeys = function (params: unknown, route?: Route, fromRoute?: Route) {
      const resolverUid = resolverUidMap.get(this)
      if (!resolverUid) {
        throw new TmLogicError('resolverUid not found for instance')
      }
      return [resolverUid, ...originalGetResolverKeys.call(this, params, route, fromRoute)]
    }

    // create proxy for constructor that adds verification and sets resolverUid
    const proxiedConstructor = new Proxy(constructor, {
      construct(Target, args) {
        const instance = new Target(...args)

        if (typeof originalGetResolverKeys !== 'function') {
          throw new TmLogicError('"withUniqueKeys" requires "getResolverKeys" method to be implemented')
        }

        // set resolverUid for this instance
        resolverUidMap.set(instance, Symbol(description))

        return instance
      },

      get(target, prop) {
        // save original name of class
        if (prop === 'name') {
          return constructor.name
        }
        // save original prototype
        if (prop === 'prototype') {
          return constructor.prototype
        }
        return target[prop]
      },
    })

    // copy all metadata from original constructor to proxy
    const metadataKeys = Reflect.getMetadataKeys?.(constructor) || []
    metadataKeys.forEach((key) => {
      const metadata = Reflect.getMetadata(key, constructor)
      Reflect.defineMetadata(key, metadata, proxiedConstructor)
    })

    // copy prototype metadata
    const prototypeMetadataKeys = Reflect.getMetadataKeys?.(constructor.prototype) || []
    prototypeMetadataKeys.forEach((key) => {
      const metadata = Reflect.getMetadata(key, constructor.prototype)
      Reflect.defineMetadata(key, metadata, proxiedConstructor.prototype)
    })

    // return proxy that saves all metadata
    return proxiedConstructor
  }
}
